import type {
  AgentEditsError,
  InstructionsEditsError,
} from "@app/lib/editor/merge_agent_suggestion_changes";
import {
  mergeAgentEdits,
  resolveInstructionsEdits,
} from "@app/lib/editor/merge_agent_suggestion_changes";
import type { MarkdownPipeline } from "@app/lib/editor/skill_instructions_html";
import { DustError } from "@app/lib/error";
import type { AgentConfigurationType } from "@app/types/assistant/agent";
import { SUPPORTED_MODEL_CONFIGS } from "@app/types/assistant/models/models";
import type { Result } from "@app/types/shared/result";
import { Err, Ok } from "@app/types/shared/result";
import type {
  AgentSuggestionType,
  ModelSuggestionType,
  SkillsSuggestionType,
  SubAgentSuggestionType,
  TagsSuggestionType,
  ToolsSuggestionType,
} from "@app/types/suggestions/agent_suggestion";
import { isCreateAgentSuggestion } from "@app/types/suggestions/agent_suggestion";

export type PreviewedAgentFields = Pick<
  AgentConfigurationType,
  | "name"
  | "description"
  | "scope"
  | "instructions"
  | "instructionsHtml"
  | "model"
  | "tags"
>;

export interface PreviewedAgentCapabilities {
  addedToolIds: string[];
  removedToolIds: string[];
  addedSkillIds: string[];
  removedSkillIds: string[];
  addedSubAgentIds: string[];
  removedSubAgentIds: string[];
}

export interface AgentSuggestionsPreview {
  fields: PreviewedAgentFields;
  capabilities: PreviewedAgentCapabilities;
}

interface PreviewAgentSuggestionsInput {
  agent: PreviewedAgentFields;
  suggestions: AgentSuggestionType[];
  pipeline: MarkdownPipeline;
}

function previewModel(
  currentModel: PreviewedAgentFields["model"],
  model: ModelSuggestionType | undefined
): Result<PreviewedAgentFields["model"], DustError<"model_not_available">> {
  if (!model) {
    return new Ok(currentModel);
  }

  const config = SUPPORTED_MODEL_CONFIGS.find(
    (m) => m.modelId === model.modelId
  );
  if (!config) {
    return new Err(
      new DustError(
        "model_not_available",
        `Model "${model.modelId}" is not supported.`
      )
    );
  }

  return new Ok({
    ...currentModel,
    providerId: config.providerId,
    modelId: config.modelId,
    reasoningEffort: model.reasoningEffort ?? config.defaultReasoningEffort,
  });
}

// Tags are suggested by name: an added tag that is not on the agent is previewed with a placeholder
// id, since it may not exist yet.
function previewTags(
  currentTags: PreviewedAgentFields["tags"],
  tags: TagsSuggestionType | undefined
): PreviewedAgentFields["tags"] {
  if (!tags) {
    return currentTags;
  }

  const removedNames = new Set(
    tags.removeTags.map((name) => name.toLowerCase())
  );
  const kept = currentTags.filter(
    (tag) => !removedNames.has(tag.name.toLowerCase())
  );
  const keptNames = new Set(kept.map((tag) => tag.name.toLowerCase()));

  return [
    ...kept,
    ...tags.addTags
      .filter((name) => !keptNames.has(name.toLowerCase()))
      .map((name) => ({
        sId: `suggested-tag-${name}`,
        name,
        kind: "standard" as const,
      })),
  ];
}

export function previewAgentSuggestions({
  agent,
  suggestions,
  pipeline,
}: PreviewAgentSuggestionsInput): Result<
  AgentSuggestionsPreview,
  AgentEditsError | InstructionsEditsError | DustError<"model_not_available">
> {
  const creation = suggestions.find(isCreateAgentSuggestion);
  if (creation) {
    const {
      name,
      description,
      instructions,
      toolIds = [],
      skillIds = [],
      subAgentIds = [],
    } = creation.suggestion;
    return new Ok({
      fields: {
        name,
        description,
        scope: agent.scope,
        model: agent.model,
        tags: agent.tags,
        instructions: agent.instructions,
        instructionsHtml: instructions,
      },
      capabilities: {
        addedToolIds: toolIds,
        removedToolIds: [],
        addedSkillIds: skillIds,
        removedSkillIds: [],
        addedSubAgentIds: subAgentIds,
        removedSubAgentIds: [],
      },
    });
  }

  const edits = mergeAgentEdits(suggestions);
  if (edits.isErr()) {
    return edits;
  }

  const {
    name,
    description,
    scope,
    instructions,
    model,
    skills = [],
    tools = [],
    subAgents = [],
    tags,
    structuredOutput,
  } = edits.value;

  const instructionsRes = resolveInstructionsEdits(
    agent,
    instructions ?? [],
    () => pipeline
  );
  if (instructionsRes.isErr()) {
    return instructionsRes;
  }

  const modelRes = previewModel(agent.model, model);
  if (modelRes.isErr()) {
    return modelRes;
  }

  return new Ok({
    fields: {
      name: name ?? agent.name,
      description: description ?? agent.description,
      scope: scope ?? agent.scope,
      model: structuredOutput
        ? {
            ...modelRes.value,
            responseFormat: structuredOutput.responseFormat ?? undefined,
          }
        : modelRes.value,
      tags: previewTags(agent.tags, tags),
      ...instructionsRes.value,
    },
    capabilities: previewCapabilities(tools, skills, subAgents),
  });
}

function previewCapabilities(
  tools: ToolsSuggestionType[],
  skills: SkillsSuggestionType[],
  subAgents: SubAgentSuggestionType[]
): PreviewedAgentCapabilities {
  return {
    addedToolIds: tools.filter((t) => t.action === "add").map((t) => t.toolId),
    removedToolIds: tools
      .filter((t) => t.action === "remove")
      .map((t) => t.toolId),
    addedSkillIds: skills
      .filter((s) => s.action === "add")
      .map((s) => s.skillId),
    removedSkillIds: skills
      .filter((s) => s.action === "remove")
      .map((s) => s.skillId),
    addedSubAgentIds: subAgents
      .filter((s) => s.action === "add")
      .map((s) => s.childAgentId),
    removedSubAgentIds: subAgents
      .filter((s) => s.action === "remove")
      .map((s) => s.childAgentId),
  };
}
